import {
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
  retry,
} from '@reduxjs/toolkit/query/react';

import { logger } from '@/utils/logger';

import { type ApiEndpoint, getApiBaseUrl } from './apiEndpoints';

/**
 * Extended fetch args that include the endpoint selector
 */
interface DynamicFetchArgs extends FetchArgs {
  endpoint?: ApiEndpoint;
}

/**
 * Token getter function - will be set by Auth provider
 * This allows the baseApi to be created before Auth0 is initialized
 */
let tokenGetter: (() => Promise<string | undefined>) | null = null;

export function setTokenGetter(getter: () => Promise<string | undefined>) {
  tokenGetter = getter;
}

const MAX_TOKEN_RETRIES = 3;
const TOKEN_RETRY_DELAY_MS = 1000;

/**
 * Get token directly from localStorage (for popup contexts like OAuth callbacks
 * where Auth0 SDK might not be fully initialized)
 */
const getTokenFromStorage = (): string | undefined => {
  // Auth0 stores tokens with keys like: @@auth0spajs@@::CLIENT_ID::AUDIENCE::openid profile email offline_access
  const keys = Object.keys(localStorage);
  const auth0Key = keys.find((key) => key.startsWith('@@auth0spajs@@'));

  if (!auth0Key) return undefined;

  try {
    const data = JSON.parse(localStorage.getItem(auth0Key) || '{}');
    return data.body?.access_token;
  } catch {
    return undefined;
  }
};

/**
 * Attempt to get the auth token, retrying with back-off if the failure
 * looks like a transient network issue (e.g. ERR_INTERNET_DISCONNECTED).
 * Falls back to localStorage if Auth0 SDK fails (for OAuth popup contexts).
 */
export const getTokenWithRetry = async (): Promise<string | undefined> => {
  if (!tokenGetter) {
    // No token getter available (e.g., in OAuth popup before Auth0 initializes)
    // Try reading directly from localStorage
    return getTokenFromStorage();
  }

  for (let attempt = 0; attempt < MAX_TOKEN_RETRIES; attempt++) {
    try {
      return await tokenGetter();
    } catch (err) {
      const isNetworkError =
        err instanceof TypeError &&
        (err.message === 'Failed to fetch' || err.message === 'Load failed');

      if (!isNetworkError || attempt === MAX_TOKEN_RETRIES - 1) {
        logger.warn('[Auth] Token fetch via Auth0 SDK failed, trying localStorage:', err);
        // Fallback to localStorage (like dataProtect does)
        return getTokenFromStorage();
      }

      const delay = TOKEN_RETRY_DELAY_MS * 2 ** attempt;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // Final fallback
  return getTokenFromStorage();
};

/**
 * Logout function - will be set by Auth provider
 * Called when API returns 401 or token acquisition fails
 */
let logoutFn: (() => void) | null = null;
let logoutTriggered = false;

export function setLogoutFn(fn: () => void) {
  logoutFn = fn;
  logoutTriggered = false;
}

function triggerLogout() {
  if (logoutFn && !logoutTriggered) {
    logoutTriggered = true;
    logger.warn('[Auth] Session expired — logging out');
    logoutFn();
  }
}

/**
 * Dynamic base query that:
 * 1. Routes requests to the correct API endpoint
 * 2. Automatically injects Auth0 bearer token (with retry on network errors)
 * 3. Handles common error scenarios
 * 4. Uses text/plain for POST/PUT/DELETE to avoid CORS preflight from localhost
 *    (backend CORS config doesn't include content-type in Access-Control-Allow-Headers)
 */
const dynamicBaseQuery: BaseQueryFn<DynamicFetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const { endpoint = 'main', ...fetchArgs } = args;
  const baseUrl = getApiBaseUrl(endpoint);

  const token = await getTokenWithRetry();

  // For requests with a body, stringify it so we can send as text/plain
  // This avoids CORS preflight since text/plain is a "simple" content type
  let modifiedArgs = fetchArgs;
  if (fetchArgs.body && typeof fetchArgs.body === 'object') {
    modifiedArgs = {
      ...fetchArgs,
      body: JSON.stringify(fetchArgs.body),
    };
  }

  const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      if (modifiedArgs.method && modifiedArgs.method !== 'GET') {
        headers.set('Accept', 'application/json');
        // Default to text/plain to avoid CORS preflight from localhost,
        // but respect explicitly set Content-Type (e.g. testConn needs application/json)
        if (
          modifiedArgs.body &&
          typeof modifiedArgs.body === 'string' &&
          !headers.has('Content-Type')
        ) {
          headers.set('Content-Type', 'text/plain;charset=UTF-8');
        }
      }
      return headers;
    },
  });

  const result = await rawBaseQuery(modifiedArgs, api, extraOptions);

  if (result.error) {
    const { error } = result;
    const { status } = error;
    const url = `${baseUrl}${fetchArgs.url ?? ''}`;

    // Determine if this is a connection test/metadata endpoint
    // These endpoints are expected to fail when credentials are invalid
    const isConnectionTest =
      url.includes('/testconnection/') ||
      url.endsWith('/testconnection') ||
      url.includes('/metadata/');
    // 404 on notification/getconfig = no config yet (expected)
    const isNotificationGetConfig = url.includes('notification/getconfig');

    const isUnauthorized =
      status === 401 ||
      (status === 'PARSING_ERROR' && 'originalStatus' in error && error.originalStatus === 401);

    if (!isConnectionTest && isUnauthorized) {
      logger.warn(`[API 401] ${url} — session expired, triggering logout`);
      triggerLogout();
      retry.fail(error);
    }

    if (status === 'FETCH_ERROR') {
      const errorMessage = 'error' in error ? error.error : 'Unknown fetch error';

      if (!navigator.onLine) {
        logger.warn(`[API OFFLINE] ${url} — browser is offline, will retry on reconnect`);
      } else {
        logger.error(`[API FETCH_ERROR] ${url} — network/CORS error:`, errorMessage);
      }

      // refetchOnReconnect handles recovery for offline scenarios
      retry.fail(error);
    }

    if (status === 'PARSING_ERROR') {
      const errorData = 'data' in error ? error.data : 'No data available';
      const preview = typeof errorData === 'string' ? errorData.slice(0, 300) : errorData;

      if (isConnectionTest) {
        logger.warn(`[API PARSING_ERROR] ${url} — credential test response not JSON:`, preview);
      } else {
        logger.error(
          `[API PARSING_ERROR] ${url} — response is not JSON. First 300 chars:`,
          preview
        );
      }
      retry.fail(error);
    }

    // Handle HTTP status codes
    const errorData = 'data' in error ? error.data : error;

    if (isConnectionTest) {
      if (status === 401 || status === 400 || status === 403) {
        logger.warn(`[API ${status}] ${url} — credential test failed:`, errorData);
      } else {
        logger.error(`[API ${status}] ${url}`, error);
      }
    } else {
      if (status === 404 && isNotificationGetConfig) {
        // 404 = no config yet, expected - queryFn handles it
      } else {
        logger.error(`[API ${status}] ${url}`, error);
      }
    }

    // Never retry 4xx client errors — the request itself is invalid
    if (typeof status === 'number' && status >= 400 && status < 500) {
      retry.fail(error);
    }
  }

  return result;
};

/**
 * Wraps the base query with RTK Query's built-in retry (exponential back-off).
 * Only 5xx server errors are retried; 4xx client errors, FETCH_ERROR, and
 * PARSING_ERROR bail immediately via retry.fail().
 */
const dynamicBaseQueryWithRetry = retry(dynamicBaseQuery, { maxRetries: 3 });

/**
 * Base API - All feature APIs inject endpoints into this
 *
 * Usage:
 * ```typescript
 * // In features/apps/api/appsApi.ts
 * import { baseApi } from '@app/baseApi';
 *
 * export const appsApi = baseApi.injectEndpoints({
 *   endpoints: (builder) => ({
 *     getApps: builder.query<App[], void>({
 *       query: () => ({ url: 'applications', endpoint: 'main' }),
 *       providesTags: ['App'],
 *     }),
 *   }),
 * });
 *
 * export const { useGetAppsQuery } = appsApi;
 * ```
 */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: dynamicBaseQueryWithRetry,
  refetchOnReconnect: true,
  tagTypes: [
    'App',
    'User',
    'CurrentUser',
    'Tenant',
    'Recipe',
    'Job',
    'Event',
    'DataFeed',
    'Connector',
    'AccessLevel',
    'Webhook',
    'Environment',
    'Transformer',
    'DPH',
    'Schedule',
    'Analytics',
    'Wizard',
    'Mapping',
    'Monitoring',
  ],
  endpoints: () => ({}),
});
